/**
 * ImpersonationBanner — Top-of-app warning + return flow
 * ═══════════════════════════════════════════════════════════════
 * SA3 follow-up (2026-04-20). When a super admin uses the "Login as"
 * feature in SAPortal, the client stashes their SA session as
 * `saToken_paused` / `saUser_paused` in localStorage and swaps in the
 * impersonated user's 30-minute JWT. This banner:
 *
 *   1. Appears at the top of the app while `localStorage.impersonating`
 *      is set with a fresh timestamp
 *   2. Counts down time remaining (JWT expires after 30 min)
 *   3. Provides a "Return to SA" button that restores the stashed
 *      SA session and navigates back to /sa
 *
 * Mounted in MainLayout so it's visible across every page.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogOut, Shield } from 'lucide-react';

const readImpersonating = () => {
  try {
    const raw = localStorage.getItem('impersonating');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Auto-expire: if the stashed expiresAt is past, treat banner as gone
    if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) return null;
    return parsed;
  } catch {
    return null;
  }
};

const formatRemaining = (expiresAt) => {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt) - new Date();
  if (ms <= 0) return 'expired';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

export const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readImpersonating());
  const [remaining, setRemaining] = useState(() => formatRemaining(session?.expiresAt));

  // Tick every second to update the countdown.
  // Also re-read from localStorage so another-tab changes propagate.
  useEffect(() => {
    if (!session) return;
    const tick = () => {
      const fresh = readImpersonating();
      if (!fresh) {
        // JWT expired or session was cleared
        setSession(null);
        return;
      }
      setRemaining(formatRemaining(fresh.expiresAt));
    };
    const id = setInterval(tick, 1000);
    // Also refresh when tab regains focus — covers edge case where the user
    // was away and the session expired while the tab was idle
    const onVisibility = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [session]);

  const handleReturn = () => {
    try {
      // Restore the paused SA session
      const pausedToken = localStorage.getItem('saToken_paused');
      const pausedUser  = localStorage.getItem('saUser_paused');
      if (pausedToken) {
        localStorage.setItem('saToken', pausedToken);
        localStorage.removeItem('saToken_paused');
      }
      if (pausedUser) {
        localStorage.setItem('saUser', pausedUser);
        localStorage.removeItem('saUser_paused');
      }
      // Clear the impersonation JWT + marker
      localStorage.removeItem('token');
      localStorage.removeItem('impersonating');
      // activeWorkspaceId is user-scoped anyway; leave it (SA portal doesn't use it)

      // Redirect back to the SA portal landing page
      const returnTo = session?.returnTo || '/sa';
      window.location.href = returnTo;  // full reload to flush all RTK caches + re-auth
    } catch (err) {
      console.error('[Impersonation] Return flow failed:', err);
      // Last-ditch: redirect anyway — worst case the user re-logs into SA
      window.location.href = '/sa';
    }
  };

  if (!session) return null;

  const expired = remaining === 'expired';

  return (
    <div
      className={`w-full flex items-center justify-between gap-3 px-4 py-2 border-b ${
        expired
          ? 'bg-red-600 border-red-700 text-white'
          : 'bg-amber-500 border-amber-600 text-white'
      }`}
      style={{ position: 'sticky', top: 0, zIndex: 60 }}
    >
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <AlertTriangle size={14} className="flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-[12px] font-bold truncate">
            Impersonating {session.user?.email || 'workspace owner'}
            {session.workspaceName && (
              <span className="font-normal opacity-90"> · {session.workspaceName}</span>
            )}
          </p>
          <p className="text-[11px] opacity-90">
            {expired
              ? 'Session expired — please return to SA'
              : `Session expires in ${remaining}. All actions are audit-logged.`}
          </p>
        </div>
      </div>
      <button
        onClick={handleReturn}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-amber-700 text-[11px] font-bold rounded-lg hover:bg-amber-50 flex-shrink-0"
      >
        <Shield size={11} /> Return to SA
      </button>
    </div>
  );
};

export default ImpersonationBanner;
